import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../api/api";
import { IOrderCategorySummary } from "../../model/IOrderSummary";
import { msg } from "../../msg";
import priceFormatter from "../../utils/priceFormatter";
import { grossPercent } from "../../utils/orderTotals";
import { sumOf, weightedMean } from "../../utils/tableTotals";
import Loading from "../common/loading/Loading";
import Table, { ITableTh } from "../common/table/Table";
import OrdersRangeFilter from "./OrdersRangeFilter";
import { SUMMARY_RIGHT, TSummaryColumn } from "./categoryColumns";
import { IDateRange } from "./orderDatePresets";

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : priceFormatter(value);

const num = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

const text = (value: string | null | undefined) => value || '';

// --
// -- A categorised order report: one row per group, the money totalled per
// -- group, and a click through to the orders behind a row.
// --
// -- Shared by the by-date, by-vendor and by-vehicle views. They overlap on
// -- eleven money columns; the caller supplies the column set and the name of
// -- the grouping column, so the differences live in one table rather than
// -- three near-identical components.
// --
// -- Nothing carries sortCol: these rows are one aggregate query's output, not
// -- a page of entities, and the server returns them already ordered.
// --
const OrdersCategoryList: FC<{
  categoryTitle: string,
  columns: TSummaryColumn[],
  rows?: IOrderCategorySummary[],
  isFetching: boolean,
  isError: boolean,
  error: unknown,
  range: IDateRange,
  onRangeChange: (range: IDateRange) => void,
  resizeStorageKey: string,
  unit: string,
  // -- How to show the grouped value, where the stored one is not what a
  // -- reader wants: by-payment-type groups on "st" and shows "Stripe". The
  // -- row still drills down on the stored value.
  formatCategory?: (category: string) => string,
  // -- Drawn between the filter row and the table, so the one date filter
  // -- above scopes the charts and the numbers alike - never a filter of its
  // -- own inside a chart.
  chart?: React.ReactNode,
  renderDrilldown: (row: IOrderCategorySummary, close: () => void) => React.ReactNode,
}> = ({
  categoryTitle,
  columns,
  rows,
  isFetching,
  isError,
  error,
  range,
  onRangeChange,
  resizeStorageKey,
  unit,
  formatCategory,
  chart,
  renderDrilldown,
}) => {
  const [open, setOpen] = useState<IOrderCategorySummary | null>(null);

  useEffect(() => {
    isError && error && toast.error(`${msg('load-orders-filed')} ${getErrorFromCatch(error)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const thead: ITableTh[] = columns.map((column) => ({
    title: column === 'category' ? categoryTitle : column,
    ...(SUMMARY_RIGHT.includes(column) ? { align: 'right' as const } : {}),
  }));

  const cellFor = (column: TSummaryColumn, row: IOrderCategorySummary) => {
    switch (column) {
      case 'category':
        // A grouped value can be empty - an item with no vehicle on it, or a
        // supplier matching no vendor. Nothing to drill into, so no link.
        return row.category
          ? (
            <button
              type="button"
              onClick={() => setOpen(row)}
              className="whitespace-nowrap font-semibold text-primary hover:text-primary_hover underline underline-offset-2 text-left"
            >
              {formatCategory ? formatCategory(row.category) : row.category}
            </button>
          )
          : <span className="text-text_pale">(none)</span>;
      case 'Count': return num(row.ordersCount);
      case 'year': return text(row.year);
      case 'make': return text(row.make);
      case 'model': return text(row.model);
      case 'part': return text(row.part);
      case 'Total_Price': return money(row.totalPrice);
      case 'PartPrice': return money(row.partPrice);
      case 'Shipping': return money(row.shipping);
      case 'SalesTax': return money(row.salesTax);
      case 'vPrice': return money(row.vendorPrice);
      case 'FeedPrice': return money(row.feedPrice);
      case 'Discount': return money(row.discount);
      case 'Gross$': return (
        <span className={(row.grossAmount ?? 0) < 0 ? 'text-pink-600 whitespace-nowrap' : 'whitespace-nowrap'}>
          {money(row.grossAmount)}
        </span>
      );
      case 'Gross%': {
        const pct = grossPercent(row.partPrice, row.grossAmount);
        return pct === null ? '' : `${pct.toFixed(2)}%`;
      }
      case 'Avg_Price': return money(row.avgPrice);
      case 'Avg_Dist': return num(row.avgDistance);
      case 'Returns': return money(row.returns);
      // by-state's spellings of three of the above - same figures, its own
      // query's headings.
      case 'Average_Price': return money(row.avgPrice);
      case 'Average_Distance': return num(row.avgDistance);
      case 'Total_Discount': return money(row.discount);
      default: return '';
    }
  };

  // --
  // -- The totals row.
  // --
  // -- These reports are not paginated, so this is a true total of the period
  // -- rather than of a page. Two columns cannot be summed, though:
  // --
  // -- - Gross% is a ratio, rebuilt from the summed part price and gross so it
  // --   is worked out exactly as each row's own is;
  // -- - Avg_Price and Avg_Dist are already averages, so they are re-averaged
  // --   weighted by each group's order count. Adding them would be nonsense
  // --   and a plain mean would let a group of one weigh as much as a group of
  // --   a thousand.
  // --
  // -- Everything else is text and stays blank.
  // --
  const totalFor = (column: TSummaryColumn, all: IOrderCategorySummary[]) => {
    switch (column) {
      case 'category': return 'Total';
      case 'Count': return num(sumOf(all, (row) => row.ordersCount));
      case 'Total_Price': return money(sumOf(all, (row) => row.totalPrice));
      case 'PartPrice': return money(sumOf(all, (row) => row.partPrice));
      case 'Shipping': return money(sumOf(all, (row) => row.shipping));
      case 'SalesTax': return money(sumOf(all, (row) => row.salesTax));
      case 'vPrice': return money(sumOf(all, (row) => row.vendorPrice));
      case 'FeedPrice': return money(sumOf(all, (row) => row.feedPrice));
      case 'Discount': return money(sumOf(all, (row) => row.discount));
      case 'Returns': return money(sumOf(all, (row) => row.returns));
      case 'Gross$': {
        const gross = sumOf(all, (row) => row.grossAmount);
        return (
          <span className={(gross ?? 0) < 0 ? 'text-pink-600 whitespace-nowrap' : 'whitespace-nowrap'}>
            {money(gross)}
          </span>
        );
      }
      case 'Gross%': {
        const pct = grossPercent(sumOf(all, (row) => row.partPrice), sumOf(all, (row) => row.grossAmount));
        return pct === null ? '' : `${pct.toFixed(2)}%`;
      }
      case 'Avg_Price': {
        const avg = weightedMean(all, (row) => row.avgPrice, (row) => row.ordersCount);
        return avg === null ? '' : money(Math.round(avg));
      }
      case 'Avg_Dist': {
        const avg = weightedMean(all, (row) => row.avgDistance, (row) => row.ordersCount);
        return avg === null ? '' : num(Math.round(avg));
      }
      // by-state's spellings. Averages weigh by group size here too - a state
      // with four orders must not count as much as one with four thousand.
      case 'Total_Discount': return money(sumOf(all, (row) => row.discount));
      case 'Average_Price': {
        const avg = weightedMean(all, (row) => row.avgPrice, (row) => row.ordersCount);
        return avg === null ? '' : money(Math.round(avg));
      }
      case 'Average_Distance': {
        const avg = weightedMean(all, (row) => row.avgDistance, (row) => row.ordersCount);
        return avg === null ? '' : num(Math.round(avg));
      }
      default: return '';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm flex flex-col flex-1 min-h-0">
      <div className="flex justify-between gap-6 items-start px-4 sm:px-6 lg:px-8 pt-5">
        <OrdersRangeFilter range={range} disabled={isFetching} onChange={onRangeChange} />
        <div className="text-text_pale whitespace-nowrap pt-7">
          {rows?.length ?? 0} {unit}
        </div>
      </div>

      {chart}

      <div className="flex-1 min-h-0 flex flex-col">
        {isFetching && !rows
          ? <div className="py-8"><Loading /></div>
          : (
            <Table
              size="small"
              fillHeight
              isResizable
              resizeStorageKey={resizeStorageKey}
              isDisablePaginate
              isPending={isFetching}
              thead={thead}
              tbody={!rows?.length
                ? undefined
                : rows.map((row) => ({ data: columns.map((column) => cellFor(column, row)) }))
              }
              tfoot={!rows?.length
                ? undefined
                : columns.map((column) => totalFor(column, rows))
              }
            />
          )
        }
      </div>

      {!!open && renderDrilldown(open, () => setOpen(null))}
    </div>
  );
};

export default OrdersCategoryList;
